import { useSyncExternalStore } from 'react';

export type ColorTheme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'tc_color_theme';
const THEME_COOKIE_KEY = 'tc_color_theme';

const THEME_CHANGE_EVENT = 'tc-color-theme-change';

// Light is the product default everywhere; the system preference is never
// consulted. The app surfaces below let a visitor opt into dark via the
// sidebar toggle, and that choice is remembered. Every other route — the
// homepage, the generated SEO landing pages behind the catch-all, blog,
// pricing, help desk, legal — is marketing and stays light unconditionally.
const THEMED_ROUTE_PREFIXES = [
  '/dashboard',
  '/admin',
  '/login',
  '/signup',
  '/register',
  '/ref',
  '/forgot',
  '/verify-email',
  '/reset-password',
  '/sso',
  '/support/live',
  '/release-control',
  '/billing',
  '/projects',
  '/settings',
  '/wallet',
  '/profile',
] as const;

const matchesRoutePrefix = (pathname: string, prefix: string): boolean => (
  pathname === prefix || pathname.startsWith(`${prefix}/`)
);

const currentPathname = (): string => (
  typeof window === 'undefined' ? '/' : window.location.pathname
);

export const isForcedLightRoute = (pathname: string = currentPathname()): boolean => {
  const normalized = pathname !== '/' ? pathname.replace(/\/+$/, '') : pathname;
  return !THEMED_ROUTE_PREFIXES.some((prefix) => matchesRoutePrefix(normalized, prefix));
};

const readThemeCookie = (): ColorTheme | null => {
  if (typeof document === 'undefined') return null;
  const value = document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(`${THEME_COOKIE_KEY}=`))
    ?.split('=')[1];
  return value === 'light' || value === 'dark' ? value : null;
};

const readStoredTheme = (): ColorTheme | null => {
  if (typeof window !== 'undefined') {
    try {
      const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
      if (stored === 'light' || stored === 'dark') return stored;
    } catch {
      // Fall through to the cookie for restricted browser contexts.
    }
  }
  return readThemeCookie();
};

export const resolveTheme = (pathname: string = currentPathname()): ColorTheme => {
  if (isForcedLightRoute(pathname)) return 'light';
  return readStoredTheme() ?? 'light';
};

export const applyTheme = (theme: ColorTheme): void => {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.theme = theme;
  document.documentElement.style.colorScheme = theme;
};

export const initializeTheme = (): ColorTheme => {
  const theme = resolveTheme();
  applyTheme(theme);
  return theme;
};

export const setTheme = (theme: ColorTheme): void => {
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      // The visual theme still applies when storage is unavailable.
    }
  }
  if (typeof document !== 'undefined') {
    document.cookie = `${THEME_COOKIE_KEY}=${theme}; Path=/; Max-Age=31536000; SameSite=Lax`;
  }
  // The choice is always stored, but a marketing route stays light regardless.
  applyTheme(resolveTheme());
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(THEME_CHANGE_EVENT));
  }
};

const getThemeSnapshot = (): ColorTheme => {
  if (typeof document === 'undefined') return 'light';
  const current = document.documentElement.dataset.theme;
  return current === 'dark' || current === 'light' ? current : resolveTheme();
};

const subscribeToTheme = (onChange: () => void): (() => void) => {
  if (typeof window === 'undefined') return () => undefined;
  // Only an explicit choice moves the theme, so a second tab changing it is
  // the one external trigger worth following.
  const handleStorage = (event: StorageEvent) => {
    if (event.key !== THEME_STORAGE_KEY) return;
    applyTheme(resolveTheme());
    onChange();
  };

  window.addEventListener(THEME_CHANGE_EVENT, onChange);
  window.addEventListener('storage', handleStorage);

  return () => {
    window.removeEventListener(THEME_CHANGE_EVENT, onChange);
    window.removeEventListener('storage', handleStorage);
  };
};

export const useTheme = (): { theme: ColorTheme; toggleTheme: () => void } => {
  const theme = useSyncExternalStore(subscribeToTheme, getThemeSnapshot, () => 'light');
  return {
    theme,
    toggleTheme: () => setTheme(theme === 'dark' ? 'light' : 'dark'),
  };
};
